import React, { useState } from "react";
import sms_icon from "../../assets/image/png/sms_icon.png";
import phone_icon from "../../assets/image/png/phone_icon.png";
import axios from "axios";

// ----- Config: dropdown + issue options -----
const PURCHASE_SOURCES = [
  "Drone Planet",
  "DSK",
];

const PRODUCT_TYPES = ["Drone", "Drone Part / Accessory"];

const ISSUE_CATEGORIES = [
  "Not Powering On",
  "Battery / Charging Issue",
  "Motor / Propeller Issue",
  "Remote Control / Connectivity Issue",
  "Camera / Gimbal Issue",
  "Firmware / App Issue",
  "Physical Damage",
  "Missing Part / Accessory",
  "Other",
];

// ----- Ticket helpers -----
// Every unique mobile number gets ONE persistent ticket number.
// If the same number submits again, the same ticket is reused
// (and the new query is appended to that ticket's history).


const initialForm = {
  name: "",
  mobile: "",
  email: "",
  purchaseSource: "",
  productType: "",
  productModel: "",
  issueCategory: "",
  description: "",
};

// Shared Tailwind class strings
const labelCls = "block text-sm font-semibold text-slate-800 mb-2";
const inputBase =
  "w-full rounded-lg border px-4 py-3 text-[15px] text-slate-800 bg-slate-50 " +
  "focus:outline-none focus:ring-4 focus:ring-sky-100 focus:border-sky-500 focus:bg-white " +
  "transition-colors placeholder:text-slate-400";
const inputValid = "border-slate-200";
const inputInvalid = "border-red-400 bg-red-50";

const ContactUsDetails = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [ticket, setTicket] = useState(null);
  const [isExisting, setIsExisting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim()))
      next.mobile = "Enter a valid 10-digit mobile number.";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim()))
      next.email = "Enter a valid email address.";
    if (!form.purchaseSource) next.purchaseSource = "Please select where you bought it from.";
    if (!form.productType) next.productType = "Please select drone or drone part.";
    if (!form.productModel.trim()) next.productModel = "Please enter the model / part name.";
    if (!form.issueCategory) next.issueCategory = "Please select the issue type.";
    if (!form.description.trim() || form.description.trim().length < 10)
      next.description = "Please describe the issue (at least 10 characters).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validate()) return;

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/support-ticket`,
      // `http://localhost:6002/support-ticket`,
      form
    );

    setTicket({
      ticketNumber: data.ticket.ticketNumber,
      name: data.ticket.name,
      mobile: data.ticket.mobile,
      email: data.ticket.email,
      purchaseSource:
        data.ticket.queries[data.ticket.queries.length - 1].purchaseSource,
      productType:
        data.ticket.queries[data.ticket.queries.length - 1].productType,
      productModel:
        data.ticket.queries[data.ticket.queries.length - 1].productModel,
      issueCategory:
        data.ticket.queries[data.ticket.queries.length - 1].issueCategory,
      description:
        data.ticket.queries[data.ticket.queries.length - 1].description,
    });

    setIsExisting(data.existing);
  } catch (error) {
    console.error(error);

    alert(
      error.response?.data?.message ||
      "Something went wrong."
    );
  }
};

  const handleNewQuery = () => {
    setForm(initialForm);
    setTicket(null);
    setErrors({});
  };

  const fieldCls = (name) =>
    `${inputBase} ${errors[name] ? inputInvalid : inputValid}`;

  return (
    <div className="py-12">
      <div className="container mx-auto max-w-6xl px-4 py-8 lg:py-12">
        {/* ---- Support / Ticket form ---- */}
        <div className="flex justify-center">
          <div className="w-full lg:w-10/12">
            <div className="bg-white border border-slate-200 shadow-xl shadow-sky-900/5 rounded-2xl px-6 py-10 sm:px-11 sm:py-12">
              <div className="text-center">
                <h4 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                  Raise a Drone Support Query
                </h4>
                <p className="text-sm text-slate-500 max-w-xl mx-auto">
                  Tell us where you bought your drone and what's wrong — we'll
                  generate a ticket number so you can track it.
                </p>
              </div>

              {!ticket ? (
                <form onSubmit={handleSubmit} noValidate className="mt-8">
                  {/* Purchase source dropdown */}
                  <div className="mb-5">
                    <label className={labelCls} htmlFor="purchaseSource">
                      Where did you buy your drone from?
                    </label>
                    <select
                      id="purchaseSource"
                      name="purchaseSource"
                      className={fieldCls("purchaseSource")}
                      value={form.purchaseSource}
                      onChange={handleChange}
                    >
                      <option value="">Select a source</option>
                      {PURCHASE_SOURCES.map((source) => (
                        <option key={source} value={source}>
                          {source}
                        </option>
                      ))}
                    </select>
                    {errors.purchaseSource && (
                      <span className="block mt-1.5 text-xs text-red-500">
                        {errors.purchaseSource}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className={labelCls} htmlFor="name">
                        Full Name
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Enter your full name"
                        className={fieldCls("name")}
                        value={form.name}
                        onChange={handleChange}
                      />
                      {errors.name && (
                        <span className="block mt-1.5 text-xs text-red-500">
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="mobile">
                        Mobile Number
                      </label>
                      <input
                        id="mobile"
                        name="mobile"
                        type="tel"
                        maxLength={10}
                        placeholder="10-digit mobile number"
                        className={fieldCls("mobile")}
                        value={form.mobile}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: "mobile",
                              value: e.target.value.replace(/\D/g, ""),
                            },
                          })
                        }
                      />
                      {errors.mobile && (
                        <span className="block mt-1.5 text-xs text-red-500">
                          {errors.mobile}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className={labelCls} htmlFor="email">
                      Email{" "}
                      <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      className={fieldCls("email")}
                      value={form.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <span className="block mt-1.5 text-xs text-red-500">
                        {errors.email}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label className={labelCls} htmlFor="productType">
                        Is this about a Drone or a Drone Part?
                      </label>
                      <select
                        id="productType"
                        name="productType"
                        className={fieldCls("productType")}
                        value={form.productType}
                        onChange={handleChange}
                      >
                        <option value="">Select an option</option>
                        {PRODUCT_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                      {errors.productType && (
                        <span className="block mt-1.5 text-xs text-red-500">
                          {errors.productType}
                        </span>
                      )}
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="productModel">
                        Drone Model / Part Name
                      </label>
                      <input
                        id="productModel"
                        name="productModel"
                        type="text"
                        placeholder="e.g., VIRAJ, Battery Pack"
                        className={fieldCls("productModel")}
                        value={form.productModel}
                        onChange={handleChange}
                      />
                      {errors.productModel && (
                        <span className="block mt-1.5 text-xs text-red-500">
                          {errors.productModel}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className={labelCls} htmlFor="issueCategory">
                      What's the issue?
                    </label>
                    <select
                      id="issueCategory"
                      name="issueCategory"
                      className={fieldCls("issueCategory")}
                      value={form.issueCategory}
                      onChange={handleChange}
                    >
                      <option value="">Select the closest match</option>
                      {ISSUE_CATEGORIES.map((issue) => (
                        <option key={issue} value={issue}>
                          {issue}
                        </option>
                      ))}
                    </select>
                    {errors.issueCategory && (
                      <span className="block mt-1.5 text-xs text-red-500">
                        {errors.issueCategory}
                      </span>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className={labelCls} htmlFor="description">
                      Describe the issue in detail
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      placeholder="What happened? When did it start? Any error messages or sounds?"
                      className={`${fieldCls("description")} resize-y`}
                      value={form.description}
                      onChange={handleChange}
                    />
                    {errors.description && (
                      <span className="block mt-1.5 text-xs text-red-500">
                        {errors.description}
                      </span>
                    )}
                  </div>

                  <div className="text-center">
                    <button
                      type="submit"
                      className="bg-gradient-to-br from-sky-500 to-blue-600 text-white font-bold text-sm sm:text-[15px] px-9 py-3.5 rounded-full shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 hover:-translate-y-0.5 active:translate-y-0 transition-all"
                    >
                      Submit Query &amp; Get Ticket Number
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center mt-8">
                  <span className="inline-block bg-emerald-50 text-emerald-600 text-xs font-bold uppercase tracking-wide px-4 py-1.5 rounded-full mb-3">
                    {isExisting ? "Existing Ticket Updated" : "New Ticket Raised"}
                  </span>
                  <h3 className="font-extrabold tracking-wide text-slate-900 text-3xl mb-1.5">
                    {ticket.ticketNumber}
                  </h3>
                  <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto">
                    {isExisting
                      ? "This mobile number already has an open ticket. Your new query has been added to it."
                      : "Please save this ticket number to track your query."}
                  </p>

                  <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 sm:px-6 py-5 text-left">
                    <div className="flex justify-between gap-5 py-2.5 border-b border-dashed border-slate-200 text-sm">
                      <span className="text-slate-500">Name</span>
                      <strong className="text-slate-900 text-right max-w-[60%]">
                        {ticket.name}
                      </strong>
                    </div>
                    <div className="flex justify-between gap-5 py-2.5 border-b border-dashed border-slate-200 text-sm">
                      <span className="text-slate-500">Mobile Number</span>
                      <strong className="text-slate-900 text-right max-w-[60%]">
                        {ticket.mobile}
                      </strong>
                    </div>
                    <div className="flex justify-between gap-5 py-2.5 border-b border-dashed border-slate-200 text-sm">
                      <span className="text-slate-500">Purchased From</span>
                      <strong className="text-slate-900 text-right max-w-[60%]">
                        {ticket.purchaseSource}
                      </strong>
                    </div>
                    <div className="flex justify-between gap-5 py-2.5 border-b border-dashed border-slate-200 text-sm">
                      <span className="text-slate-500">Product Type</span>
                      <strong className="text-slate-900 text-right max-w-[60%]">
                        {ticket.productType}
                      </strong>
                    </div>
                    <div className="flex justify-between gap-5 py-2.5 border-b border-dashed border-slate-200 text-sm">
                      <span className="text-slate-500">Model / Part</span>
                      <strong className="text-slate-900 text-right max-w-[60%]">
                        {ticket.productModel}
                      </strong>
                    </div>
                    <div className="flex justify-between gap-5 py-2.5 border-b border-dashed border-slate-200 text-sm">
                      <span className="text-slate-500">Issue</span>
                      <strong className="text-slate-900 text-right max-w-[60%]">
                        {ticket.issueCategory}
                      </strong>
                    </div>
                    <div className="flex flex-col gap-1.5 py-2.5 text-sm">
                      <span className="text-slate-500">Description</span>
                      <strong className="text-slate-900 font-semibold">
                        {ticket.description}
                      </strong>
                    </div>
                  </div>

                  <button
                    onClick={handleNewQuery}
                    className="mt-6 bg-gradient-to-br from-sky-500 to-blue-600 text-white font-bold text-sm sm:text-[15px] px-9 py-3.5 rounded-full shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 hover:-translate-y-0.5 active:translate-y-0 transition-all"
                  >
                    Raise Another Query
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ---- Contact cards ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
          <div className="bg-sky-50 text-center px-6 py-10 rounded-2xl">
            <img width={80} src={sms_icon} alt="sms_icon" className="mx-auto" />
            <h5 className="text-xl font-bold text-slate-900 mt-3 mb-0">
              Write to us at:
            </h5>
            <a
              href="mailto:support@droneplanet.com"
              className="block text-2xl font-bold text-slate-700 mt-3 no-underline hover:text-sky-600 transition-colors"
            >
              support@droneplanet.in
            </a>
          </div>
          <div className="bg-sky-50 text-center px-6 py-10 rounded-2xl">
            <img width={80} src={phone_icon} alt="phone_icon" className="mx-auto" />
            <h5 className="text-xl font-bold text-slate-900 mt-3 mb-0">
              Call us on:
            </h5>
            <a
              href="tel:+919773571992"
              className="block text-2xl font-bold text-slate-700 mt-3 no-underline hover:text-sky-600 transition-colors"
            >
              +91 97735 71992
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsDetails;